import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import moment, { Moment, MomentInput } from 'moment';
import styled from 'styled-components';
import { Button, DateField } from '../../elements';
import devices from '../../styles/devices';
import getErrorMsg from '~/helpers/errorMessages';

interface ICustomDate {
  type: string;
  onSubmit: () => void;
}

const Container = styled.form`
  display: flex;
  flex-direction: column;
  margin: 0 20px;

  @media (min-width: ${devices.mobile}) {
    margin: 0;
  }

  .MuiFormLabel-root {
    color: ${({ theme }) => theme.colors.gray400} !important;
  }
  .MuiInputBase-input {
    color: ${({ theme }) => theme.colors.gray400} !important;
  }
  .MuiInput-underline::before {
    border-color: ${({ theme }) => theme.colors.gray200} !important;
  }
  .MuiInputAdornment-root {
    color: ${({ theme }) => theme.colors.gray400} !important;
  }
  label + .MuiInput-formControl {
    margin: 15px 0;
  }
  .MuiButtonBase-root {
    align-self: center;
  }
`;
const Title = styled.p`
  color: ${({ theme }) => theme.colors.gray400};
  margin: 15px 0 10px;
`;
const InputsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 25px;

  .MuiIconButton-root {
    color: ${({ theme }) => theme.colors.gray400};
  }

  @media (min-width: ${devices.mobile}) {
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 20px;
  }
`;

export default function CustomDate({
  type,
  onSubmit,
}: ICustomDate): JSX.Element {
  const {
    handleSubmit,
    errors,
    watch,
    formState,
    getValues,
    setValue,
    ...formControl
  } = useForm({
    mode: 'onChange',
  });
  const [minEndDateValue, setMinEndDateValue] = useState<Moment | null>(null);
  const currentDate = new Date();

  // This value is being used to control the second input (minDate), so it's not related
  // with the value currently selected in the first input
  function minEndDate(date: Date) {
    setValue('endDate', null);

    const sumADay = moment(date).add(1, 'days');
    setMinEndDateValue(sumADay);
  }

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <Title>Selecione um período:</Title>
      <InputsContainer>
        {/* @ts-ignore */}
        <DateField
          ariaLabel="Período inicial"
          fullWidth
          name="startDate"
          label="De *"
          formControl={formControl}
          type="date"
          error={!!errors?.startDate}
          caption={getErrorMsg(errors, 'startDate')}
          minDate={moment(currentDate).subtract(1, 'years')}
          maxDate={moment(currentDate).subtract(1, 'days')}
          onAccept={minEndDate}
          rules={{
            required: true,
            validate: (val: MomentInput) => {
              const date = moment(val, 'DD/MM/YYYY');
              return date.isValid() && moment().diff(date, 'days') <= 365;
            },
          }}
        />
        {/* @ts-ignore */}
        <DateField
          ariaLabel="Período final"
          fullWidth
          name="endDate"
          label="Até *"
          formControl={formControl}
          type="date"
          error={!!errors?.endDate}
          caption={getErrorMsg(errors, 'endDate')}
          disabled={!watch('startDate')}
          minDate={minEndDateValue}
          maxDate={currentDate}
          rules={{
            required: true,
            validate: (val: MomentInput) => {
              const date = moment(val, 'DD/MM/YYYY');
              const starDate = getValues('startDate');
              const endDate = getValues('endDate');
              const tempo = Math.abs(starDate.getTime() - endDate.getTime()); // Subtrai uma data pela outra
              const dias = Math.ceil(tempo / (1000 * 60 * 60 * 24));
              return date.isValid() && dias < 140;
            },
          }}
        />
        {/* @ts-ignore */}
        <Button
          ariaLabel="Filtrar por período"
          fullWidth
          color={type}
          variant="outlined"
          label="Filtrar por período"
          type="submit"
          disabled={!formState.isValid}
        />
      </InputsContainer>
    </Container>
  );
}
